/**
 * Blanks benefit-definition defaults on SBS cells the plan has no stored value for.
 *
 * `buildRowData` skips a definition's `defaultValue` only for `isPlaceholder`
 * plans. A column materialized from a placeholder is no longer a placeholder, so
 * every `defaultValue` in the coverage reappears on cells the analyst never
 * filled in — Critical Illness renders "Not Included" for Benefit Maximum -
 * Spouse over a NULL column, and the numeric amount rows seed 1.
 *
 * Keyed per cell rather than per plan: a column that holds one real employee
 * value is not "empty", but its untouched spouse cells still must not read as
 * answered.
 *
 * Quotes-only — injected through `PlanEditor.transformRowData`, so the Build
 * surface keeps seeding its defaults as before.
 */

import type { BenefitDefinition, RfpCoverage } from "@platform/plan-grid-core";

/** A grid row carrying a benefit definition that declares a default value. */
type SeededDefaultRow = Record<string, unknown> & {
  benefit: BenefitDefinition;
  benefitKey: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasStoredValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== "";

/**
 * Benefit keys each plan holds a real value for, keyed by plan id.
 *
 * Built from the coverage `PlanEditor` actually receives, so values patched in
 * upstream (the voluntary life Benefits Included toggles that
 * `applyBenefitsIncludedDefaults` deliberately resolves to "no", and any
 * captured dependent-toggle overrides) count as stored and survive.
 */
const storedKeysByPlanId = (
  coverage: RfpCoverage | undefined,
): Map<string, Set<string>> =>
  new Map(
    (coverage?.benefitPlans ?? []).flatMap((plan) => {
      const benefits = plan.benefits as Record<string, unknown> | undefined;
      if (!plan.id) return [];

      return [
        [
          plan.id,
          new Set(
            Object.entries(benefits ?? {})
              .filter(([, value]) => hasStoredValue(value))
              .map(([key]) => key),
          ),
        ] as const,
      ];
    }),
  );

const isSeededDefaultRow = (row: unknown): row is SeededDefaultRow => {
  if (!isRecord(row)) return false;

  const benefit = row["benefit"];

  return (
    isRecord(benefit) &&
    benefit["defaultValue"] !== undefined &&
    typeof row["benefitKey"] === "string"
  );
};

/**
 * The value a cell carries when the plan stored nothing for it.
 *
 * Array-typed cells get `undefined` rather than `[]`: an empty array is a real
 * answer for some fields — dental's Service Waiting Periods renders it as
 * "None" — so handing one to a column that stored nothing asserts something the
 * analyst never entered. Every array cell renderer already guards on a falsy
 * value.
 */
const blankValue = (benefit: BenefitDefinition): unknown =>
  benefit.cell?.["dataType"] === "array" ? undefined : "";

/** Network-dependent rows hold one value per tier rather than a scalar. */
const blankPlanValue = (
  benefit: BenefitDefinition,
  current: unknown,
): unknown => {
  if (benefit.networkDependent === true && isRecord(current)) {
    return Object.fromEntries(
      Object.keys(current).map((network) => [network, blankValue(benefit)]),
    );
  }

  return blankValue(benefit);
};

const suppressRow = (
  row: SeededDefaultRow,
  storedKeys: Map<string, Set<string>>,
): SeededDefaultRow => {
  const blanked = Object.fromEntries(
    [...storedKeys]
      .filter(([planId, keys]) => planId in row && !keys.has(row.benefitKey))
      .map(([planId]) => [planId, blankPlanValue(row.benefit, row[planId])]),
  );

  return Object.keys(blanked).length > 0 ? { ...row, ...blanked } : row;
};

/**
 * Builds a `PlanEditor.transformRowData` callback that blanks seeded defaults on
 * cells with no stored value. Compose it after any other row transform so it
 * observes the final row set.
 *
 * @param coverage - The coverage handed to `PlanEditor`, after any upstream
 *   benefits patching
 */
export const buildSuppressSeededDefaults = (
  coverage: RfpCoverage | undefined,
): ((rows: unknown[]) => unknown[]) => {
  const storedKeys = storedKeysByPlanId(coverage);

  return (rows: unknown[]): unknown[] => {
    if (storedKeys.size === 0) return rows;

    return rows.map((row) =>
      isSeededDefaultRow(row) ? suppressRow(row, storedKeys) : row,
    );
  };
};
